/**
 * Network Context Provider
 * Central source of truth for online/offline status across the application
 * Handles connectivity detection and automatic sync triggers
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useElectron } from '@/hooks/useElectron';

// Conflict data type for sync conflicts
export interface SyncConflict {
  id: string;
  entityType: 'order' | 'order_item' | 'category' | 'menu_item';
  entityId: string;
  field: string;
  localValue: unknown;
  serverValue: unknown;
  localUpdatedAt: string;
  serverUpdatedAt: string;
  description?: string;
}

export type ConflictResolution = 'keep_local' | 'use_server' | 'merge';

interface NetworkContextType {
  isOnline: boolean;
  isElectronApp: boolean;
  connectionQuality: 'good' | 'poor' | 'offline';
  lastOnlineAt: Date | null;
  pendingSyncCount: number;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: string | null;
  // Conflict management
  conflicts: SyncConflict[];
  hasConflicts: boolean;
  resolveConflict: (conflictId: string, resolution: ConflictResolution) => void;
  resolveAllConflicts: (resolution: ConflictResolution) => void;
  dismissConflicts: () => void;
  // Actions
  forceSync: () => Promise<void>;
  refreshMenu: () => Promise<void>;
  prefetchMenu: (branchId: string) => Promise<{ 
    success: boolean; 
    categoriesCount?: number;
    itemsCount?: number;
    variantsCount?: number;
    error?: string 
  }>;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};

interface NetworkProviderProps {
  children: React.ReactNode;
}

export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const {
    isElectron: isElectronApp,
    isOnline: electronIsOnline,
    syncStatus,
    forceSync: electronForceSync,
    refreshMenu: electronRefreshMenu,
    prefetchMenu: electronPrefetchMenu,
  } = useElectron();

  // Track online status - use browser API for web, Electron API for desktop
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true;
  });

  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(null);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'poor' | 'offline'>('good');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  
  // Conflict state
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);

  // Debounce ref to prevent rapid status changes
  const statusChangeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Conflict handlers
  const resolveConflict = useCallback((conflictId: string, resolution: ConflictResolution) => {
    console.log(`[Network] Resolving conflict ${conflictId} with ${resolution}`);
    // In a real implementation, this would call the Electron IPC to apply the resolution
    // For now, we just remove the conflict from the list
    setConflicts(prev => prev.filter(c => c.id !== conflictId));
    
    // TODO: Call Electron IPC to actually apply the resolution
    // if (isElectronApp && window.electronAPI?.resolveConflict) {
    //   window.electronAPI.resolveConflict(conflictId, resolution);
    // }
  }, []);

  const resolveAllConflicts = useCallback((resolution: ConflictResolution) => {
    console.log(`[Network] Resolving all ${conflicts.length} conflicts with ${resolution}`);
    // Apply resolution to all conflicts
    conflicts.forEach(conflict => {
      resolveConflict(conflict.id, resolution);
    });
    setConflicts([]);
  }, [conflicts, resolveConflict]);

  const dismissConflicts = useCallback(() => {
    console.log('[Network] Dismissing all conflicts');
    setConflicts([]);
  }, []);

  // Update from Electron status
  useEffect(() => {
    if (isElectronApp) {
      setIsOnline(electronIsOnline);
      if (electronIsOnline) {
        setLastOnlineAt(new Date());
        setConnectionQuality('good');
      } else {
        setConnectionQuality('offline');
      }
    }
  }, [isElectronApp, electronIsOnline]);

  // Update from sync status (including conflicts)
  useEffect(() => {
    if (syncStatus) {
      setIsSyncing(syncStatus.isSyncing);
      setPendingSyncCount(syncStatus.pendingCount);
      setSyncError(syncStatus.error);
      if (syncStatus.lastSyncAt) {
        setLastSyncAt(new Date(syncStatus.lastSyncAt));
      }
      // Handle conflicts from sync status
      if (syncStatus.conflicts && Array.isArray(syncStatus.conflicts)) {
        setConflicts(syncStatus.conflicts as SyncConflict[]);
      }
    }
  }, [syncStatus]);

  // Listen to browser online/offline events (for both web and as backup for Electron)
  useEffect(() => {
    const handleOnline = () => {
      // Debounce to prevent rapid flapping
      if (statusChangeTimeout.current) {
        clearTimeout(statusChangeTimeout.current);
      }
      
      statusChangeTimeout.current = setTimeout(() => {
        console.log('[Network] Connection restored');
        setIsOnline(true);
        setLastOnlineAt(new Date());
        setConnectionQuality('good');
        
        // Auto-trigger sync on reconnection in Electron
        if (isElectronApp && electronForceSync) {
          console.log('[Network] Triggering auto-sync after reconnection');
          electronForceSync().catch(err => {
            console.error('[Network] Auto-sync failed:', err);
          });
        }
      }, 500); // 500ms debounce
    };

    const handleOffline = () => {
      if (statusChangeTimeout.current) {
        clearTimeout(statusChangeTimeout.current);
      }
      
      statusChangeTimeout.current = setTimeout(() => {
        console.log('[Network] Connection lost');
        setIsOnline(false);
        setConnectionQuality('offline');
      }, 500);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (statusChangeTimeout.current) {
        clearTimeout(statusChangeTimeout.current);
      }
    };
  }, [isElectronApp, electronForceSync]);

  // Connection quality check (ping test)
  useEffect(() => {
    if (!isOnline) {
      setConnectionQuality('offline');
      return;
    }

    // Periodic connection quality check - use a lightweight approach
    const checkConnectionQuality = async () => {
      if (!isOnline) return;
      
      try {
        const start = Date.now();
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        
        // Use a simple connectivity check - just fetch a tiny resource
        // We don't need authentication for this, just checking if we can reach the internet
        // Using a small, fast endpoint that doesn't require auth
        await fetch('https://www.google.com/generate_204', {
          method: 'HEAD',
          mode: 'no-cors', // Avoid CORS issues
          signal: controller.signal,
        });
        
        clearTimeout(timeout);
        const latency = Date.now() - start;
        
        if (latency > 2000) {
          setConnectionQuality('poor');
        } else {
          setConnectionQuality('good');
        }
      } catch {
        // Don't set offline here, just mark as poor quality
        // The browser events will handle actual offline detection
        if (isOnline) {
          setConnectionQuality('poor');
        }
      }
    };

    // Check immediately and then every 60 seconds (reduced frequency)
    checkConnectionQuality();
    const interval = setInterval(checkConnectionQuality, 60000);

    return () => clearInterval(interval);
  }, [isOnline]);

  // Force sync wrapper
  const forceSync = useCallback(async () => {
    if (!isElectronApp || !electronForceSync) {
      console.warn('[Network] forceSync only available in Electron app');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const result = await electronForceSync();
      if (result) {
        setLastSyncAt(new Date());
        if (!result.success && result.errors.length > 0) {
          setSyncError(result.errors[0]);
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Sync failed';
      setSyncError(error);
      console.error('[Network] Force sync error:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isElectronApp, electronForceSync]);

  // Refresh menu wrapper
  const refreshMenu = useCallback(async () => {
    if (!isElectronApp || !electronRefreshMenu) {
      console.warn('[Network] refreshMenu only available in Electron app');
      return;
    }

    try {
      await electronRefreshMenu();
      console.log('[Network] Menu refresh completed');
    } catch (err) {
      console.error('[Network] Menu refresh error:', err);
      throw err;
    }
  }, [isElectronApp, electronRefreshMenu]);

  // Prefetch menu wrapper - caches menu data for offline use
  const prefetchMenu = useCallback(async (branchId: string) => {
    if (!isElectronApp || !electronPrefetchMenu) {
      console.warn('[Network] prefetchMenu only available in Electron app');
      return { success: false, error: 'Not in Electron app' };
    }

    if (!isOnline) {
      console.warn('[Network] Cannot prefetch menu while offline');
      return { success: false, error: 'Offline - cannot prefetch' };
    }

    try {
      console.log(`[Network] Prefetching menu for branch: ${branchId}`);
      const result = await electronPrefetchMenu(branchId);
      if (result?.success) {
        console.log(`[Network] Menu prefetch completed: ${result.categoriesCount} categories, ${result.itemsCount} items`);
      }
      return result || { success: false, error: 'No result' };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Prefetch failed';
      console.error('[Network] Menu prefetch error:', err);
      return { success: false, error };
    }
  }, [isElectronApp, electronPrefetchMenu, isOnline]);

  const value: NetworkContextType = {
    isOnline,
    isElectronApp,
    connectionQuality,
    lastOnlineAt,
    pendingSyncCount,
    isSyncing,
    lastSyncAt,
    syncError,
    // Conflict management
    conflicts,
    hasConflicts: conflicts.length > 0,
    resolveConflict,
    resolveAllConflicts,
    dismissConflicts,
    // Actions
    forceSync,
    refreshMenu,
    prefetchMenu,
  };

  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkContext;
